import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useTenantAuth } from '@/auth/TenantAuthContext';
import {
  IconArrowDownCircle,
  IconArrowLeft,
  IconArrowUpCircle,
  IconChevronRight,
  IconClose,
  IconDashboard,
  IconMenu,
  IconReceipt,
  IconStore,
  IconTrend,
} from '@/components/Icons';
import { LogoMark } from '@/components/Logo';
import { UserMenu } from '@/components/UserMenu';
import { useAiCredits } from '@/features/ask-pilot/api';
import { useExitImpersonation } from '@/features/impersonation/useImpersonation';
import { FilterBar } from '@/features/portal/FilterBar';
import { PortalContextProvider } from '@/features/portal/PortalContext';
import type { PortalContextValue } from '@/features/portal/PortalContext';
import { fetchBranches, fetchDevices, fetchProfile, portalKeys } from '@/features/portal/api';
import { useReportFilters } from '@/features/portal/filters';
import { API_URL } from '@/lib/api';

interface NavItem {
  to: string;
  label: string;
  /** Top-level items only. Sub-items sit under a group header that has one. */
  icon?: ReactNode;
  end?: boolean;
}

/** A collapsible group of report pages, mirroring the POS's own side menu. */
interface NavGroup {
  label: string;
  icon: ReactNode;
  items: readonly NavItem[];
}

/**
 * Grouped to match the till's menu, so an owner who knows the POS knows where to
 * look here. Dashboard stays on its own — it is one page, and a group of one is
 * just a link wearing a disclosure triangle.
 *
 * Most pages under `/r/:key` are data-driven: the server describes each report
 * (see extra-reports.ts) and one page renders them all. Expenses is the
 * exception by design — its own route, its own menu entry.
 */
const NAV_DASHBOARD: NavItem = {
  to: '/',
  label: 'Dashboard',
  icon: <IconDashboard className="h-4 w-4" />,
  end: true,
};

/** Top of the Reports group, only for tenants whose plan (or a boost) includes AI. */
const NAV_ASK_PILOT: NavItem = { to: '/ask-pilot', label: 'Ask Pilot ✦' };
const REPORTS_GROUP = 'Reports';

const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Sales',
    icon: <IconReceipt className="h-4 w-4" />,
    items: [
      { to: '/orders', label: 'Order history' },
      { to: '/sales-returns', label: 'Return history' },
      { to: '/deliveries', label: 'Delivery history' },
    ],
  },
  {
    label: REPORTS_GROUP,
    icon: <IconTrend className="h-4 w-4" />,
    items: [
      { to: '/sales', label: 'Sales report' },
      { to: '/products', label: 'Product sales' },
      { to: '/shifts', label: 'Shift report' },
      { to: '/ledger', label: 'Ledger' },
      { to: '/r/day-book', label: 'Day book' },
      { to: '/r/vat', label: 'Tax report' },
    ],
  },
  {
    label: 'Sales analysis',
    icon: <IconTrend className="h-4 w-4" />,
    items: [
      { to: '/r/staff-sales', label: 'Staff & waiter sales' },
      { to: '/r/hourly-sales', label: 'Hourly sales' },
      { to: '/r/discounts', label: 'Discounts' },
      { to: '/r/cancelled-returned', label: 'Cancelled & returned' },
    ],
  },
  {
    label: 'Expenses',
    icon: <IconArrowUpCircle className="h-4 w-4" />,
    items: [{ to: '/expenses', label: 'Expense report' }],
  },
  {
    label: 'Kitchen',
    icon: <IconStore className="h-4 w-4" />,
    items: [
      { to: '/r/kot-history', label: 'KOT history' },
      { to: '/r/edited-orders', label: 'Edited orders' },
      { to: '/r/kot-stations', label: 'KOT assignment' },
    ],
  },
  {
    label: 'Catalog',
    icon: <IconStore className="h-4 w-4" />,
    items: [
      { to: '/inventory', label: 'Inventory' },
      { to: '/r/low-stock', label: 'Low stock' },
    ],
  },
  {
    label: 'Purchases',
    icon: <IconStore className="h-4 w-4" />,
    items: [
      { to: '/purchases', label: 'Purchase list' },
      { to: '/purchase-returns', label: 'Purchase return' },
    ],
  },
  {
    label: 'Finance',
    icon: <IconArrowDownCircle className="h-4 w-4" />,
    items: [
      { to: '/receivables', label: 'Receivables' },
      { to: '/payables', label: 'Payables' },
    ],
  },
  {
    label: 'People',
    icon: <IconArrowDownCircle className="h-4 w-4" />,
    items: [
      { to: '/customers', label: 'Customers' },
      { to: '/r/customer-insights', label: 'Customer insights' },
      { to: '/r/delivery-boys', label: 'Delivery boys' },
      { to: '/r/tables', label: 'Tables' },
      { to: '/r/users', label: 'Users & roles' },
    ],
  },
];

/** Shown only to tenants the SA has given the smart menu. */
const NAV_SMART_MENU: NavGroup = {
  label: 'Smart menu',
  icon: <IconStore className="h-4 w-4" />,
  items: [{ to: '/smart-menu', label: 'QR menu' }],
};

/**
 * One collapsible group.
 *
 * A real <button> with aria-expanded/aria-controls, not a styled div: the header
 * is the only way to open the group, so it has to be reachable by keyboard and
 * announced as a disclosure.
 *
 * The open state is deliberately LOCAL rather than in the URL. It is a view
 * preference, not part of what the page shows — putting it in the query string
 * would make two links to the same report differ, and land in the report cache
 * key.
 */
function NavGroupSection({
  group,
  search,
  defaultOpen,
}: {
  group: NavGroup;
  search: string;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  // Navigating INTO a collapsed group (via a link elsewhere, back/forward, or a
  // pasted URL) has to open it. Without this the sidebar would show no active
  // item at all and the reader would have no idea where they are.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      >
        {group.icon}
        <span className="flex-1 text-left">{group.label}</span>
        <IconChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
      </button>

      {/* Unmounted rather than hidden when closed: a NavLink that is not on
          screen should not be tabbable. */}
      {open ? (
        <div id={panelId} className="mt-1 space-y-1">
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={{ pathname: item.to, search }}
              end={item.end}
              // Indented to sit under the group header rather than beside it.
              className={navSubItemClass}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** True when [pathname] is inside [group] — drives which group opens on load. */
function groupHoldsPath(group: NavGroup, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return group.items.some(
    (item) => path === item.to || path.startsWith(`${item.to}/`),
  );
}

/**
 * Which routes get the range/branch/till chrome.
 *
 * Not the dashboard: it is a fixed at-a-glance overview of its own window, so a
 * range picker there would be a control that changes nothing visible on the
 * page that owns it. Pick a range on Sales report instead.
 *
 * Not a single order either — it is addressed by id, so a date range cannot
 * include or exclude it.
 */
function showsFilters(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return false;
  // Settings, not a report: no date range or branch applies.
  if (path === '/smart-menu') return false;
  // Ask Pilot picks its own period from the question.
  if (path === '/ask-pilot' || path.startsWith('/ask-pilot/')) return false;
  return !/^\/orders\/.+/.test(path);
}

/**
 * Sub-items get the indent and no icon. The group header already carries the
 * icon, and a second column of them would just add noise at this depth.
 */
function navSubItemClass({ isActive }: { isActive: boolean }): string {
  const base =
    'flex items-center rounded-lg py-1.5 pl-10 pr-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
  return isActive
    ? `${base} bg-brand-600/[0.12] font-semibold text-brand-700`
    : `${base} text-slate-600 hover:bg-slate-100 hover:text-slate-900`;
}

function navItemClass({ isActive }: { isActive: boolean }): string {
  const base =
    'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
  return isActive
    ? `${base} bg-brand-600/[0.12] text-brand-700`
    : `${base} text-slate-700 hover:bg-slate-200 hover:text-slate-900`;
}

export function PortalLayout() {
  const { user, logout } = useTenantAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const exitImpersonation = useExitImpersonation();

  const profileQuery = useQuery({
    queryKey: portalKeys.me(),
    queryFn: ({ signal }) => fetchProfile(signal),
    staleTime: 5 * 60_000,
  });

  // The profile cached at login renders the shell before /portal/me resolves.
  const profile = profileQuery.data ?? user;
  const timezone = profile?.timezone ?? '';
  const currency = profile?.currency ?? '';
  // `tenantLogoUrl` is relative to the API origin, same as every other
  // server-relative path — prefixed here, not stored absolute, so it stays
  // correct if VITE_API_URL ever changes without a re-login.
  const tenantLogoSrc = profile?.tenantLogoUrl ? `${API_URL}${profile.tenantLogoUrl}` : undefined;

  const branchesQuery = useQuery({
    queryKey: portalKeys.branches(),
    queryFn: ({ signal }) => fetchBranches(signal),
    staleTime: 5 * 60_000,
  });
  const branches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data]);
  const multiBranch = branches.length > 1;

  const devicesQuery = useQuery({
    queryKey: portalKeys.devices(),
    queryFn: ({ signal }) => fetchDevices(signal),
    staleTime: 5 * 60_000,
  });
  const devices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data]);
  const multiDevice = devices.length > 1;

  const filters = useReportFilters(timezone);
  const filtersVisible = showsFilters(location.pathname);
  const busy = useIsFetching({ queryKey: portalKeys.all }) > 0;

  // A branch id in the URL that this tenant cannot use (single-branch tenant, or
  // a link from another tenant) must not silently filter every report to zero.
  const selectedBranchId = filters.filters.branchId;
  const { setBranch } = filters;
  useEffect(() => {
    if (branchesQuery.data === undefined || selectedBranchId === null) return;
    if (!multiBranch || !branches.some((branch) => branch.id === selectedBranchId)) {
      setBranch(null);
    }
  }, [branchesQuery.data, branches, multiBranch, selectedBranchId, setBranch]);

  // Same reasoning as the branch guard above: a till id from a stale bookmark or
  // another tenant's link would otherwise 404 every report on the page.
  const selectedDeviceId = filters.filters.deviceId;
  const { setDevice } = filters;
  useEffect(() => {
    if (devicesQuery.data === undefined || selectedDeviceId === null) return;
    if (!devices.some((device) => device.id === selectedDeviceId)) {
      setDevice(null);
    }
  }, [devicesQuery.data, devices, selectedDeviceId, setDevice]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
    setSigningOut(true);
    void logout().finally(() => {
      setSigningOut(false);
      navigate('/login', { replace: true });
    });
  }, [logout, navigate]);

  const aiCredits = useAiCredits();
  const aiEnabled = aiCredits.data?.enabled === true;
  const navGroups = useMemo(() => {
    const groups = aiEnabled
      ? NAV_GROUPS.map((group) =>
          group.label === REPORTS_GROUP ? { ...group, items: [NAV_ASK_PILOT, ...group.items] } : group,
        )
      : NAV_GROUPS;
    return profile?.smartMenuEnabled ? [...groups, NAV_SMART_MENU] : groups;
  }, [aiEnabled, profile?.smartMenuEnabled]);

  const contextValue = useMemo<PortalContextValue>(
    () => ({
      profile: profile ?? null,
      currency,
      timezone,
      branches,
      multiBranch,
      devices,
      multiDevice,
      filters,
    }),
    [profile, currency, timezone, branches, multiBranch, devices, multiDevice, filters],
  );

  const sidebar = (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <LogoMark className="h-9 w-9" src={tenantLogoSrc} />
        <div className="min-w-0">
          <p
            className="truncate text-sm font-semibold text-slate-900"
            title={profile?.tenantName ?? undefined}
          >
            {profile?.tenantName ?? 'Order Pilot'}
          </p>
          <p className="truncate text-xs text-slate-500">Reports</p>
        </div>
      </div>

      <nav aria-label="Reports" className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        <NavLink
          // Carries the branch / till filters while switching reports. The
          // period is deliberately left behind: every report opens on Today.
          to={{ pathname: NAV_DASHBOARD.to, search: filters.search }}
          end
          className={navItemClass}
        >
          {NAV_DASHBOARD.icon}
          {NAV_DASHBOARD.label}
        </NavLink>

        {navGroups.map((group) => (
          <NavGroupSection
            key={group.label}
            group={group}
            search={filters.search}
            // Opens itself when the current page is inside it, so a reload or a
            // shared deep link never lands you in a collapsed menu with no clue
            // where you are.
            defaultOpen={groupHoldsPath(group, location.pathname)}
          />
        ))}
      </nav>

      {/* Account + Sign out live in the top-right app bar — see <UserMenu>. */}
    </div>
  );

  return (
    <PortalContextProvider value={contextValue}>
      <div className="min-h-screen bg-surface">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-slate-200 md:block">{sidebar}</aside>

        {/* Mobile drawer */}
        {menuOpen ? (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 animate-fade-in bg-slate-900/50" onClick={() => setMenuOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-64 shadow-xl">
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="btn btn-ghost absolute right-2 top-3 h-8 w-8 p-0 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
                aria-label="Close menu"
              >
                <IconClose className="h-4 w-4" />
              </button>
              {sidebar}
            </div>
          </div>
        ) : null}

        <div className="md:pl-64">
          {/* App bar: drawer toggle + wordmark on mobile, account menu always. */}
          <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="btn btn-ghost h-9 w-9 p-0 md:hidden"
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <IconMenu className="h-5 w-5" />
            </button>
            <LogoMark className="h-7 w-7 md:hidden" src={tenantLogoSrc} />
            <span className="truncate text-sm font-semibold text-slate-900 md:hidden">
              {profile?.tenantName ?? 'Reports'}
            </span>

            {/* Impersonation lives in the app bar, beside the account it is
                impersonating, rather than as a banner over the report.
                As a full-width Alert it pushed every page down, competed with
                the page heading for first read, and repeated an explanation the
                operator already saw in the confirmation dialog. Here it is
                permanently visible, next to the identity it qualifies, and the
                way out sits where the way in was. `status` + aria-label keep it
                announced without the banner's bulk. */}
            {profile?.impersonated === true ? (
              <div
                role="status"
                className="ml-auto flex min-w-0 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1"
              >
                <span className="hidden text-xs font-medium text-amber-900 sm:inline">
                  Viewing as
                </span>
                <span
                  className="max-w-[9rem] truncate text-xs font-semibold text-amber-900"
                  title={profile.tenantName}
                >
                  {profile.tenantName}
                </span>
                {/* Clears the TENANT session only — the SA session is a separate
                    store, so this lands back on /cp still signed in. */}
                <button
                  type="button"
                  onClick={exitImpersonation}
                  aria-label={`Exit viewing as ${profile.tenantName} and return to the admin console`}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
                >
                  <IconArrowLeft className="h-3.5 w-3.5" />
                  Exit
                </button>
              </div>
            ) : null}

            <div className={profile?.impersonated === true ? '' : 'ml-auto'}>
              <UserMenu
                name={profile?.username ?? 'Signed in'}
                email={profile?.email}
                // Which restaurant these reports belong to — worth showing here
                // because a super admin can be viewing as any tenant.
                context={profile?.tenantName}
                onSignOut={handleLogout}
                signingOut={signingOut}
              />
            </div>
          </div>

          {filtersVisible ? (
            <FilterBar
              filters={filters}
              branches={branches}
              showBranch={multiBranch}
              devices={devices}
              showDevice={multiDevice}
              busy={busy}
            />
          ) : null}

          <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </main>
        </div>
      </div>
    </PortalContextProvider>
  );
}
